"use client";

import {
  ArrowRightIcon,
  ChevronDownIcon,
  ClockIcon,
  FileTextIcon,
  SearchIcon,
  ShieldAlertIcon,
  UserIcon,
} from "lucide-react";
import { memo, useState } from "react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Skeleton } from "@/components/ui/skeleton";
import { promptGenerators } from "@/hooks/use-quick-action";
import { cn } from "@/lib/utils";
import { AddressDisplay } from "./address-display";
import { ChainBadge, TransactionStatusBadge } from "./chain-badge";
import { CollapsibleCard } from "./collapsible-card";
import { QuickActionsBar } from "./quick-actions-bar";
import type {
  DecodedLog,
  QuickAction,
  TransactionDetailsProps,
  TransactionDetails as TransactionDetailsType,
} from "./types";
import { getChainInfo } from "./types";
import { GasDisplay, TimeAgo, ValueDisplay } from "./value-display";

/**
 * Main TransactionDetails component - displays full transaction information
 */
const PureTransactionDetails = ({
  cardId,
  input,
  output,
  state,
  onQuickAction,
  isReadonly,
}: TransactionDetailsProps) => {
  const _chain = getChainInfo(input.chainId);

  // Handle loading states
  if (state === "input-streaming" || state === "input-available") {
    return (
      <TransactionDetailsSkeleton
        chainId={input.chainId}
        txHashes={input.transactions}
      />
    );
  }

  // Handle error state
  if (output && "error" in output) {
    return (
      <TransactionDetailsError
        chainId={input.chainId}
        error={output.error}
        onQuickAction={onQuickAction}
        suggestions={output.suggestions}
        txHashes={input.transactions}
      />
    );
  }

  // Handle empty output
  if (!output || output.length === 0) {
    return (
      <TransactionDetailsError
        chainId={input.chainId}
        error="No transaction data found"
        onQuickAction={onQuickAction}
        txHashes={input.transactions}
      />
    );
  }

  // Render transaction details
  return (
    <div className="space-y-4">
      {output.map((tx, idx) => (
        <TransactionCard
          cardId={`${cardId}-tx-${tx.hash || idx}`}
          chainId={input.chainId}
          key={tx.hash}
          onQuickAction={onQuickAction}
          transaction={tx}
        />
      ))}
    </div>
  );
};

export const TransactionDetails = memo(PureTransactionDetails);

/**
 * Individual transaction card
 */
type TransactionCardProps = {
  cardId: string;
  transaction: TransactionDetailsType;
  chainId: string;
  onQuickAction: TransactionDetailsProps["onQuickAction"];
};

function TransactionCard({
  cardId,
  transaction,
  chainId,
  onQuickAction,
}: TransactionCardProps) {
  const [logsOpen, setLogsOpen] = useState(false);
  const chain = getChainInfo(chainId);

  const quickActions: QuickAction[] = [
    {
      label: "Explain this tx",
      icon: <SearchIcon className="size-3" />,
      prompt: promptGenerators.explainTransaction(transaction.hash, chain.name),
    },
    {
      label: `Who is ${transaction.from.slice(0, 8)}...?`,
      icon: <UserIcon className="size-3" />,
      prompt: promptGenerators.aboutAddress(
        transaction.from,
        "sent this transaction"
      ),
    },
    {
      label: "More txs from sender",
      icon: <FileTextIcon className="size-3" />,
      prompt: promptGenerators.moreTransactions(transaction.from, chain.name),
    },
    {
      label: "Is this suspicious?",
      icon: <ShieldAlertIcon className="size-3" />,
      prompt: promptGenerators.checkTransactionSafety(transaction.hash),
    },
  ];

  // Header content (always visible when collapsed)
  const headerContent = (
    <div
      className={cn(
        "flex items-center justify-between border-b bg-background/50 p-4 pr-12",
        chain.bgGradient
      )}
    >
      <div className="flex items-center gap-3">
        <div
          className="flex size-10 items-center justify-center rounded-full"
          style={{ backgroundColor: `${chain.color}20` }}
        >
          <span className="font-bold text-lg" style={{ color: chain.color }}>
            &lt;&gt;
          </span>
        </div>
        <div>
          <div className="flex items-center gap-2">
            <AddressDisplay
              address={transaction.hash}
              chainId={chainId}
              endChars={6}
              showCopy
              showExplorerLink
              size="md"
              startChars={10}
            />
          </div>
          <div className="flex items-center gap-2 text-muted-foreground text-xs">
            <ClockIcon className="size-3" />
            <TimeAgo timestamp={transaction.timestamp} />
            <span>-</span>
            <span>Block {transaction.blockNumber}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <ChainBadge chainId={chainId} showFullName size="md" />
        <TransactionStatusBadge size="md" status={transaction.status} />
      </div>
    </div>
  );

  return (
    <CollapsibleCard
      cardId={cardId}
      className={cn("bg-gradient-to-br", chain.bgGradient)}
      headerContent={headerContent}
    >
      {/* Flow visualization */}
      <div className="border-b bg-background/30 p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="mb-1 text-muted-foreground text-xs">From</div>
            <AddressDisplay
              address={transaction.from}
              chainId={chainId}
              onClick={() =>
                onQuickAction(
                  promptGenerators.aboutAddress(
                    transaction.from,
                    "sent this transaction"
                  )
                )
              }
              showCopy
              showExplorerLink
              size="md"
            />
          </div>

          <div className="flex flex-col items-center gap-1">
            <ArrowRightIcon className="size-5 text-muted-foreground" />
            <ValueDisplay
              className="font-semibold"
              decimals={18}
              size="md"
              symbol={chain.nativeCurrency.symbol}
              value={transaction.value}
            />
          </div>

          <div className="min-w-0 flex-1 text-right">
            <div className="mb-1 text-muted-foreground text-xs">
              {transaction.to ? "To" : "Contract Created"}
            </div>
            {transaction.to ? (
              <AddressDisplay
                address={transaction.to}
                chainId={chainId}
                onClick={() =>
                  onQuickAction(
                    promptGenerators.aboutAddress(
                      transaction.to!,
                      "received this transaction"
                    )
                  )
                }
                showCopy
                showExplorerLink
                size="md"
              />
            ) : (
              <AddressDisplay
                address={transaction.contractCreated ?? "Unknown"}
                chainId={chainId}
                isContract
                showCopy
                showExplorerLink
                size="md"
              />
            )}
          </div>
        </div>
      </div>

      {/* Gas info */}
      <div className="flex items-center justify-between border-b bg-background/30 px-4 py-2">
        <span className="text-muted-foreground text-xs">Gas</span>
        <GasDisplay
          gasPrice={transaction.gasPrice}
          gasUsed={transaction.gasUsed}
          nativeSymbol={chain.nativeCurrency.symbol}
        />
      </div>

      {/* Token transfers */}
      {transaction.tokenTransfers && transaction.tokenTransfers.length > 0 && (
        <div className="border-b bg-background/30 p-4">
          <div className="mb-2 font-medium text-muted-foreground text-xs uppercase tracking-wider">
            Token Transfers ({transaction.tokenTransfers.length})
          </div>
          <div className="space-y-2">
            {transaction.tokenTransfers.slice(0, 5).map((transfer, idx) => (
              <div
                className="flex items-center justify-between rounded-lg bg-muted/30 p-2 text-sm"
                key={`${transfer.from}-${transfer.to}-${transfer.token.address}-${transfer.value}-${idx}`}
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium">{transfer.token.symbol}</span>
                  <span className="text-muted-foreground">→</span>
                  <AddressDisplay
                    address={transfer.to}
                    chainId={chainId}
                    showCopy={false}
                    showExplorerLink={false}
                    size="sm"
                  />
                </div>
                <ValueDisplay
                  decimals={transfer.token.decimals}
                  size="sm"
                  symbol={transfer.token.symbol}
                  value={transfer.value}
                />
              </div>
            ))}
            {transaction.tokenTransfers.length > 5 && (
              <div className="text-center text-muted-foreground text-xs">
                +{transaction.tokenTransfers.length - 5} more transfers
              </div>
            )}
          </div>
        </div>
      )}

      {/* Logs */}
      {transaction.logs && transaction.logs.length > 0 && (
        <Collapsible onOpenChange={setLogsOpen} open={logsOpen}>
          <CollapsibleTrigger className="flex w-full items-center justify-between border-b bg-background/30 px-4 py-3 transition-colors hover:bg-muted/30">
            <span className="flex items-center gap-2 font-medium text-sm">
              <FileTextIcon className="size-4" />
              Event Logs ({transaction.logs.length})
            </span>
            <ChevronDownIcon
              className={cn(
                "size-4 transition-transform",
                logsOpen && "rotate-180"
              )}
            />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="max-h-64 space-y-2 overflow-y-auto bg-background/30 p-4">
              {transaction.logs.map((log, idx) => (
                <LogEntry
                  chainId={chainId}
                  key={`${log.address}-${log.topics?.[0] ?? ""}-${idx}`}
                  log={log}
                />
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Quick actions */}
      <div className="bg-background/50 p-4">
        <QuickActionsBar
          actions={quickActions}
          onAction={onQuickAction}
          size="sm"
        />
      </div>
    </CollapsibleCard>
  );
}

/**
 * Log entry display
 */
function LogEntry({ log, chainId }: { log: DecodedLog; chainId: string }) {
  return (
    <div className="rounded-lg bg-muted/50 p-3 font-mono text-xs">
      <div className="mb-2 flex items-center justify-between">
        <span className="font-medium font-sans text-foreground">
          {log.decoded?.eventName ?? "Unknown Event"}
        </span>
        <AddressDisplay
          address={log.address}
          chainId={chainId}
          showCopy={false}
          showExplorerLink={false}
          size="sm"
        />
      </div>
      {log.decoded?.args && (
        <div className="space-y-1 text-muted-foreground">
          {Object.entries(log.decoded.args).map(([key, value]) => (
            <div className="flex" key={key}>
              <span className="w-24 shrink-0 text-muted-foreground/70">
                {key}:
              </span>
              <span className="truncate">{value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Skeleton loader for transaction details
 */
function TransactionDetailsSkeleton({
  txHashes = [],
  chainId,
}: {
  txHashes?: string[];
  chainId: string;
}) {
  const chain = getChainInfo(chainId);

  // If no hashes provided, show a single skeleton
  const hashesToRender = txHashes.length > 0 ? txHashes : ["loading"];

  return (
    <div className="space-y-4">
      {hashesToRender.map((hash) => (
        <div
          className={cn(
            "overflow-hidden rounded-xl border bg-gradient-to-br",
            chain.bgGradient
          )}
          key={hash}
        >
          {/* Header skeleton */}
          <div className="flex items-center justify-between border-b bg-background/50 p-4">
            <div className="flex items-center gap-3">
              <Skeleton className="size-10 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-3 w-32" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Skeleton className="h-6 w-16 rounded-full" />
              <Skeleton className="h-6 w-16 rounded-full" />
            </div>
          </div>

          {/* Flow skeleton */}
          <div className="border-b bg-background/30 p-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1 space-y-2">
                <Skeleton className="h-3 w-10" />
                <Skeleton className="h-4 w-32" />
              </div>
              <div className="flex flex-col items-center gap-1">
                <Skeleton className="size-5 rounded" />
                <Skeleton className="h-4 w-20" />
              </div>
              <div className="flex-1 space-y-2 text-right">
                <Skeleton className="ml-auto h-3 w-10" />
                <Skeleton className="ml-auto h-4 w-32" />
              </div>
            </div>
          </div>

          {/* Gas skeleton */}
          <div className="flex items-center justify-between border-b bg-background/30 px-4 py-2">
            <Skeleton className="h-3 w-10" />
            <Skeleton className="h-3 w-32" />
          </div>

          {/* Loading indicator */}
          <div className="flex items-center justify-center gap-2 bg-background/50 p-4">
            <div className="size-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <span className="text-muted-foreground text-sm">
              Fetching transaction details...
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Error state for transaction details
 */
function TransactionDetailsError({
  error,
  suggestions,
  txHashes,
  chainId,
  onQuickAction,
}: {
  error: string;
  suggestions?: string[];
  txHashes: string[];
  chainId: string;
  onQuickAction: TransactionDetailsProps["onQuickAction"];
}) {
  return (
    <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-4">
      <div className="flex items-start gap-3">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-destructive/20">
          <ShieldAlertIcon className="size-4 text-destructive" />
        </div>
        <div className="flex-1">
          <h4 className="font-medium text-destructive">
            Failed to fetch transaction
          </h4>
          <p className="mt-1 text-destructive/80 text-sm">{error}</p>
          {txHashes.length > 0 && (
            <div className="mt-2 text-destructive/70 text-xs">
              Transaction: {txHashes[0]}
            </div>
          )}
          {suggestions && suggestions.length > 0 && (
            <div className="mt-3 border-destructive/20 border-t pt-3">
              <p className="mb-2 font-medium text-destructive/70 text-xs">
                Suggestions:
              </p>
              <ul className="space-y-1 text-destructive/60 text-xs">
                {suggestions.map((suggestion, idx) => (
                  <li className="flex items-start gap-2" key={idx}>
                    <span>•</span>
                    <span>{suggestion}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
